import { Border, TextSize, VictoryLegend } from 'victory';
import { Label } from '../Label';
import { patterns } from './patterns/Patterns';
import { getBarChartProps } from './helpers/getBarChartProps';
import { colors } from '@/uiPrimitives/color/colors.css';
import { useCbsGraphMeasurements } from '../useCbsGraphMeasurements';
import { fontStyle } from '../cbsGraph.config';

const getBarChartLegendData = ({
	legendLabel,
	index,
	containerId,
}: {
	legendLabel: string;
	index: number;
	containerId: string;
}) => {
	const { id, color } = patterns[index];

	return {
		name: legendLabel,
		symbol: {
			type: 'square',
			fill: `url(#${containerId}-${id}) ${color}`,
			stroke: color,
			strokeWidth: 0.5,
		},
	};
};

const legendSymbolSpacer = 12;
const legendSymbolWidth = 15;
const legendPadding = 4;
const boldCompensationFactor = 1.08;

const barChartLegendMinInlineSize = 100;

export const getLegendWidth = ({
	labels,
	title,
	hasLegend,
}: {
	labels: Array<string>;
	title: string;
	hasLegend: boolean;
}) => {
	if (!hasLegend) return 0;

	const { width } = TextSize.approximateTextSize(labels, fontStyle);

	const contentWidth =
		width + legendSymbolWidth + legendSymbolSpacer + 3 * legendPadding;
	const { width: titleTextWidth } = TextSize.approximateTextSize(
		title,
		fontStyle,
	);

	const titleWidth =
		titleTextWidth * boldCompensationFactor + 3 * legendPadding;

	const legendWidth = Math.max(
		barChartLegendMinInlineSize,
		contentWidth,
		titleWidth,
	);

	return legendWidth;
};

export const Legend = ({
	valuesByComparingDimension,
	legend,
	containerId,
}: Pick<
	ReturnType<typeof getBarChartProps>,
	'valuesByComparingDimension' | 'legend'
> & {
	containerId: string;
}) => {
	const { graphInlineSize } = useCbsGraphMeasurements();

	if (!legend.hasLegend) return null;

	const legendWidth = getLegendWidth(legend);

	return (
		<VictoryLegend
			title={legend.title}
			orientation="vertical"
			rowGutter={-6}
			width={legendWidth}
			x={graphInlineSize - legendWidth}
			y={0}
			symbolSpacer={legendSymbolSpacer}
			borderPadding={legendPadding}
			style={{
				labels: fontStyle,
				title: { ...fontStyle, fontWeight: 'bold' },
				border: {
					fill: colors.shape.negative.blue,
				},
			}}
			borderComponent={<Border width={getLegendWidth(legend)} />}
			data={[...valuesByComparingDimension]
				.reverse()
				.map(({ legendLabel }, index) =>
					getBarChartLegendData({
						containerId,
						legendLabel,
						index: valuesByComparingDimension.length - 1 - index,
					}),
				)}
			labelComponent={Label({
				verticalAnchor: 'start',
				dy: -9,
			})}
		/>
	);
};
